import React, { useEffect, useRef, useState } from 'react';
import { Headphones, Loader2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Article narration player. First tap on Listen kicks the backend to
 *  generate (or return cached) MP3 via ElevenLabs → renders a slim audio
 *  bar. Fails softly if the API key isn't configured or the provider
 *  rejects the request. */
const ArticleListenBar = ({ slug, bodyLocked }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [audioUrl, setAudioUrl] = useState('');
  const [error, setError] = useState('');
  const [cached, setCached] = useState(false);
  const [hasMusic, setHasMusic] = useState(false);
  const [disclaimer, setDisclaimer] = useState('');
  const audioRef = useRef(null);

  useEffect(() => {
    // Reset when the article changes.
    setStatus('idle');
    setAudioUrl('');
    setError('');
  }, [slug]);

  const generate = async () => {
    setStatus('loading');
    setError('');
    try {
      const r = await fetch(`${API}/api/articles/${slug}/tts`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Narration failed');
      setAudioUrl(data.audio_url);
      setCached(!!data.cached);
      setHasMusic(!!data.has_music);
      setDisclaimer(data.voice_disclaimer || '');
      setStatus('ready');
      // Small delay so <audio> mounts before play.
      setTimeout(() => audioRef.current?.play?.().catch(() => {}), 200);
    } catch (e) {
      setError(e.message);
      setStatus('error');
    }
  };

  if (bodyLocked) return null;

  return (
    <div
      data-testid="article-listen-bar"
      className="mb-6 rounded-lg border border-red-900/40 bg-zinc-950/70 px-4 py-3 flex items-center gap-3 flex-wrap"
    >
      {status !== 'ready' ? (
        <>
          <Headphones className="h-4 w-4 text-red-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-400">Listen to this story</p>
            <p className="text-xs text-zinc-400 leading-snug">Narrated and scored like a news bulletin. Included with your subscription.</p>
          </div>
          <button
            type="button"
            onClick={generate}
            disabled={status === 'loading'}
            data-testid="article-listen-btn"
            className="inline-flex items-center gap-2 h-9 px-4 rounded bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-[0.2em] transition-colors disabled:opacity-60"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…
              </>
            ) : (
              <>▶ Play</>
            )}
          </button>
        </>
      ) : (
        <>
          <Headphones className="h-4 w-4 text-emerald-400 shrink-0" />
          <audio
            ref={audioRef}
            src={audioUrl}
            controls
            preload="auto"
            data-testid="article-listen-audio"
            className="flex-1 min-w-0 h-9"
          />
          {hasMusic && (
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-red-400/80" data-testid="article-listen-music-badge">
              Scored
            </span>
          )}
          {cached && <span className="text-[9px] font-black uppercase tracking-[0.25em] text-emerald-400/80">Cached</span>}
        </>
      )}
      {status === 'ready' && disclaimer && (
        <p className="w-full text-[11px] text-zinc-500 leading-snug mt-1" data-testid="article-listen-disclaimer">
          {disclaimer}
        </p>
      )}
      {status === 'error' && (
        <div className="w-full flex items-start gap-2 text-red-400 text-xs mt-1" data-testid="article-listen-error">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ArticleListenBar;
